import React from 'react';
import { Link } from 'react-router-dom';
import MagneticHover from '../components/MagneticHover';
import OptimizedImage from '../components/OptimizedImage';
import { imageConfig, altTexts } from '../utils/imageConfig';

const Home = () => {
  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero section">
        <div className="container">
          <div className="grid grid-2" style={{ alignItems: 'center', gap: '3rem' }}>
            <div className="hero-content fade-in-up">
              <h1 className="hero-title">
                Transform Your Business with AI-Powered Solutions
              </h1>
              <p className="subtitle fade-in-up" style={{animationDelay: '0.2s'}}>
                Modernise technology, improve delivery, and achieve measurable outcomes.
              </p>
              <div className="hero-actions fade-in-up" style={{animationDelay: '0.4s'}}>
                <Link to="/contact#contact-form" className="btn btn-primary">
                  Start Your Transformation
                </Link>
                <Link to="/services" className="btn btn-secondary">
                  Explore Our Services
                </Link>
              </div>
            </div>
            <div className="hero-image">
              <OptimizedImage
                src={imageConfig.hero.londonSkyline}
                alt={altTexts.hero.londonSkyline}
                width="100%"
                height="400px"
                className="hero-main-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="section section-dark">
        <div className="container">
          <h2 className="section-title fade-in-up">Our Core Services</h2>
          <div className="grid grid-4 animate-stagger">
            <div className="card magnetic fade-in-up liquid-morph">
              <OptimizedImage
                src={imageConfig.services.aiStrategy}
                style={{ objectFit: 'cover', aspectRatio: '4/3' }}
                alt={altTexts.services.aiStrategy}
                width="100%"
                height="200px"
                className="card-image"
              />
              <h3 className="card-title">Transformation Capabilities</h3>
              <p className="card-description">
                Building capabilities for sustainable change and growth.
              </p>
            </div>
            <div className="card magnetic fade-in-up morphing-card">
              <OptimizedImage
                src={imageConfig.services.transformationAssurance}
                alt="Transformation Assurance and Quality Management"
                width="100%"
                height="200px"
                className="card-image"
                style={{ objectFit: 'cover', aspectRatio: '4/3' }}
              />
              <h3 className="card-title">Transformation Assurance</h3>
              <p className="card-description">
                Ensuring transformations deliver intended outcomes.
              </p>
            </div>
            <div className="card magnetic fade-in-up liquid-morph">
              <OptimizedImage
                src={imageConfig.technology.delivery}
                alt="Portfolio and Programme Delivery Management"
                width="100%"
                height="200px"
                className="card-image"
                style={{ objectFit: 'cover', aspectRatio: '4/3' }}
              />
              <h3 className="card-title">Portfolio & Programme Delivery</h3>
              <p className="card-description">
                Managing complex portfolios and programmes effectively.
              </p>
            </div>
            <div className="card magnetic fade-in-up morphing-card mobile-hide-card">
              <OptimizedImage
                src={imageConfig.services.enterpriseSolutions}
                alt="Enterprise solutions for large and small businesses"
                width="100%"
                height="200px"
                className="card-image"
                style={{ objectFit: 'cover', aspectRatio: '4/3' }}
              />
              <h3 className="card-title">Helping Large & Small Businesses</h3>
              <p className="card-description">
                Tailored solutions for businesses of all sizes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section">
        <div className="container">
          <div className="grid grid-2">
            <div className="fade-in-left">
              <h2 className="section-title">Why Choose Saner Consulting?</h2>
              <p className="subtitle">
                We combine proven transformation methods with practical AI adoption.
              </p>
              <ul className="benefits-list">
                <li className="fade-in-up" style={{animationDelay: '0.1s'}}>✓ Practical AI implementation with measurable value</li>
                <li className="fade-in-up" style={{animationDelay: '0.2s'}}>✓ Structured transformation methods that reduce risk</li>
                <li className="fade-in-up" style={{animationDelay: '0.3s'}}>✓ End-to-end delivery from strategy to execution</li>
                <li className="fade-in-up" style={{animationDelay: '0.4s'}}>✓ Strong governance, assurance, and outcomes focus</li>
              </ul>
              <Link to="/contact#contact-form" className="btn btn-primary fade-in-up" style={{animationDelay: '0.5s'}}>
                Get Started Today
              </Link>
            </div>
            <div className="stats-grid animate-stagger fade-in-right">
              <MagneticHover intensity={0.2}>
                <div className="stat-card scale-in morphing-card">
                  <div className="stat-number">25+</div>
                  <div className="stat-label">Years of Extensive Experience</div>
                </div>
              </MagneticHover>
              <MagneticHover intensity={0.2}>
                <div className="stat-card scale-in liquid-morph">
                  <div className="stat-number">5</div>
                  <div className="stat-label">Countries of Global Experience</div>
                </div>
              </MagneticHover>
              <MagneticHover intensity={0.2}>
                <div className="stat-card scale-in morphing-card">
                  <div className="stat-number">£500m</div>
                  <div className="stat-label">Annual Investment Value Supported</div>
                </div>
              </MagneticHover>
              <MagneticHover intensity={0.2}>
                <div className="stat-card scale-in liquid-morph">
                  <div className="stat-number">5+</div>
                  <div className="stat-label">Sectors - Experience Across Many Different Business Sectors</div>
                </div>
              </MagneticHover>
              <MagneticHover intensity={0.2}>
                <div className="stat-card scale-in morphing-card">
                  <div className="stat-number">200+</div>
                  <div className="stat-label">Transformation Initiatives Delivered</div>
                </div>
              </MagneticHover>
            </div>
          </div>
        </div>
      </section>

      {/* Emerging Technology Insights */}
      <section className="section section-dark">
        <div className="container">
          <h2 className="section-title fade-in-up">The Future is Now: Emerging Technology Leadership</h2>
          <p className="subtitle fade-in-up" style={{animationDelay: '0.2s'}}>
            Emerging technologies are changing how organisations deliver outcomes. We help you adopt what works and avoid noise.
          </p>
          
          <div className="insights-grid animate-stagger">
            <div className="insight-card fade-in-up">
              <div className="insight-stat">96%</div>
              <p className="insight-text">
                of leaders expect digital integration to reshape their sector this decade
              </p>
            </div>
            
            <div className="insight-card fade-in-up">
              <div className="insight-stat">95%</div>
              <p className="insight-text">
                of executives see advanced computing as a key breakthrough driver
              </p>
            </div>
            
            <div className="insight-card fade-in-up mobile-hide-insight">
              <div className="insight-stat">93%</div>
              <p className="insight-text">
                of leaders say rapid change requires focused innovation strategy
              </p>
            </div>
            
            <div className="insight-card fade-in-up mobile-hide-insight">
              <div className="insight-stat">83%</div>
              <p className="insight-text">
                of executives view science-driven tech as key for major societal challenges
              </p>
            </div>
          </div>
          
          <div className="emerging-tech-cta fade-in-up" style={{animationDelay: '0.8s'}}>
            <div className="grid grid-2" style={{ alignItems: 'center', gap: '2rem' }}>
              <div>
                <h3 className="card-title">Position Your Organization Forward</h3>
                <p className="card-description">
                  Prioritise the right technologies and turn strategy into delivery.
                </p>
                <Link to="/contact#contact-form" className="btn btn-primary">
                  Start Your Transformation
                </Link>
              </div>
              <div>
                <OptimizedImage
                  src={imageConfig.hero.businessMeeting}
                  alt="Position Your Organization at the Forefront"
                  width="100%"
                  height="400px"
                  className="cta-image"
                  style={{ objectFit: 'cover', aspectRatio: '16/9' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section background-shift">
        <div className="container">
          <div className="cta-content fade-in-up">
            <h2 className="section-title">
              Ready to Transform Your Business?
            </h2>
            <p className="subtitle fade-in-up" style={{animationDelay: '0.2s'}}>
              Let's discuss how Saner Consulting can help you achieve your transformation goals.
            </p>
            <Link to="/contact#contact-form" className="btn btn-primary">
              Get Started Today
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
